import dayjs from 'dayjs';

// Ranges for color hashing
const hRange = [0, 360];
const sRange = [50, 75];
const lRange = [25, 60];

/**
 * This method is used to hash a string to generate a random color.
 * @param str - The input string to hash.
 * @returns {number} - The hash value.
 */
const getHashOfString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  hash = Math.abs(hash);
  return hash;
};

/**
 * Normalize the hash color in a custom range between min and max.
 * @param hash - The hash value to normalize.
 * @param min - The minimum range value.
 * @param max - The maximum range value.
 * @returns {number} - The normalized hash value.
 */
const normalizeHash = (hash: number, min: number, max: number): number => {
  return Math.floor((hash % (max - min)) + min);
};

/**
 * Generate random hue, saturation, and lightness based on string hashing and normalize them.
 * @param name - The input string or number.
 * @returns {number[]} - The generated HSL values.
 */
const generateHSL = (name: string | number): number[] => {
  const hash = getHashOfString(name.toString());
  const h = normalizeHash(hash, hRange[0], hRange[1]);
  const s = normalizeHash(hash, sRange[0], sRange[1]);
  const l = normalizeHash(hash, lRange[0], lRange[1]);
  return [h, s, l];
};

/**
 * Generate a random color with HSL base and add opacity.
 * @param id - The input string or number.
 * @param opacity - The opacity value (optional, default is 1 for full opacity).
 * @returns {string} - The generated color in HSLA format.
 */
export const getRandomAvatarColor = (
  id: string | number,
  opacity: number = 1,
): string => {
  if (!id) {
    return `rgba(27,160,164,${opacity})`;
  }
  const hsl = generateHSL(id);
  return `hsla(${hsl[0]}, ${hsl[1]}%, ${hsl[2]}%, ${opacity})`;
};

/**
 * Function to generate an avatar name from a full name.
 * @param fullName - The full name as a string.
 * @returns A string containing the first letter of the first name and the first letter of the last name, or just the first letter if only one word is provided.
 */
export const getAvatarName = (fullName: string): string => {
  // Trim the input string to remove any leading/trailing whitespace
  fullName = fullName.trim();

  // Split the full name into an array of words
  const nameParts = fullName.split(' ');

  // If there's only one word, return the first letter of that word
  if (nameParts.length === 1) {
    return nameParts[0].charAt(0).toUpperCase();
  }

  // Otherwise, return the first letter of the first name and the first letter of the last name
  const firstNameInitial = nameParts[0].charAt(0).toUpperCase();
  const lastNameInitial = nameParts[nameParts.length - 1]
    .charAt(0)
    .toUpperCase();
  return firstNameInitial + lastNameInitial;
};

export const formatBubbleMessageDate = (dateStr: string): string => {
  const now = dayjs();
  const messageDate = dayjs(dateStr);

  // Check if the date is valid
  if (!messageDate.isValid()) {
    return '';
  }

  // Compare the message date to the current date to determine the appropriate format.
  if (now.isSame(messageDate, 'year')) {
    if (now.isSame(messageDate, 'day')) {
      // Same day: Show time in 12-hour format with AM/PM.
      return messageDate.format('h:mm A');
    } else if (now.isSame(messageDate, 'week')) {
      // Same week: Show day of the week and time.
      return messageDate.format('ddd [at] h:mm A');
    } else {
      // Same year but different week: Show month, day, and time.
      return messageDate.format('MMM D [at] h:mm A');
    }
  } else {
    // Different year: Show full date with month, day, year, and time.
    return messageDate.format('MMM D, YYYY [at] h:mm A');
  }
};
